import { TimeInForceType } from '@/compiled_proto/com/celertech/positionmanager/api/enums/TimeInForceTypeProto';
import { defaultValues } from '@/components/form/LimitOrderForm';
import { useAppSelector } from '@/state/hooks';
import { selectSettings } from '@/state/reducers/settingSlice';
import { LumeFxSide } from '@/types/lumeFx';
import BigNumber from 'bignumber.js';
import { useCallback, useEffect, useMemo } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { invalidLimitPriceTooltip } from './useOrderBook';
import { UseOrderExecutionReturn } from './useOrderExecution';

export interface UseLimitOrderReturn {
    limitPriceTooltip: string;
    isRestingLimitOrder: boolean;
    resetDefault: (...args) => void;
}

const useLimitOrder = (
    formInstance: UseFormReturn<any>,
    orderExecutionProps: UseOrderExecutionReturn,
    idPrefix: string
): UseLimitOrderReturn => {
    const settings = useAppSelector(selectSettings);

    const { reset, watch, setValue } = formInstance;
    const { sideState, isCcy2Order, orderBook, instrument } = orderExecutionProps;
    const { asks, bids, bestAsk, bestBid, worstAsk, worstBid, nextWorstAsk, nextWorstBid } = orderBook;
    const { defaultSlippage } = instrument;

    const limitPriceKey = `${idPrefix}limitPrice`;

    const side = sideState[0];

    const [limitPrice, duration, quantity] = watch([limitPriceKey, 'duration', `${idPrefix}quantity`]);

    const isRestingLimitOrder = useMemo(() => {
        if (duration?.value) {
            return [TimeInForceType.DAY, TimeInForceType.GTC].includes(+duration.value as TimeInForceType);
        }
        return false;
    }, [duration]);

    const limitPriceTooltip = useMemo(() => {
        let tooltip: any = '';
        if (isRestingLimitOrder && limitPrice && bestAsk && bestBid) {
            if (side === LumeFxSide.BUY) {
                if (isCcy2Order && limitPrice < bestBid) {
                    tooltip = invalidLimitPriceTooltip(side, bestBid, isCcy2Order);
                } else if (!isCcy2Order && limitPrice > bestAsk) {
                    tooltip = invalidLimitPriceTooltip(side, bestAsk, isCcy2Order);
                }
            }
            if (side === LumeFxSide.SELL) {
                if (isCcy2Order && limitPrice > bestAsk) {
                    tooltip = invalidLimitPriceTooltip(side, bestAsk, isCcy2Order);
                } else if (!isCcy2Order && limitPrice < bestBid) {
                    tooltip = invalidLimitPriceTooltip(side, bestBid, isCcy2Order);
                }
            }
        }
        return tooltip;
    }, [bestAsk, bestBid, side, limitPrice, isRestingLimitOrder]);

    const resetDefault = useCallback(
        (limitPrice?: number, quantity?: number) => {
            // resets all fields, set limit price to default worst price
            const resetValues: typeof defaultValues = { ...defaultValues, slippage: defaultSlippage };
            if (side === LumeFxSide.BUY && asks) resetValues['limitPrice'] = worstAsk;
            else if (side === LumeFxSide.SELL && bids) resetValues['limitPrice'] = worstBid;
            if (limitPrice && quantity) {
                reset({ ...resetValues, limitPrice, quantity, duration: settings.trading.defaultLimitOrderDuration });
            } else reset({ ...resetValues, duration: settings.trading.defaultLimitOrderDuration });
        },
        [defaultValues, defaultSlippage, side, worstAsk, worstBid]
    );

    useEffect(() => {
        if (isRestingLimitOrder) {
            if (bestAsk && bestBid) {
                const limitPrice = BigNumber(bestAsk).plus(bestBid).dividedBy(2).toNumber();
                setValue(limitPriceKey, limitPrice, { shouldValidate: true });
                setValue(`${idPrefix}slippage`, '0', { shouldValidate: true });
            }
        }
    }, [isRestingLimitOrder]);

    useEffect(() => {
        if (!isRestingLimitOrder) {
            if (side === LumeFxSide.BUY && asks) {
                if (quantity) setValue(limitPriceKey, nextWorstAsk(quantity), { shouldValidate: true });
                else setValue(limitPriceKey, worstAsk, { shouldValidate: true });
            } else if (side === LumeFxSide.SELL && bids) {
                if (quantity) setValue(limitPriceKey, nextWorstBid(quantity), { shouldValidate: true });
                else setValue(limitPriceKey, worstBid, { shouldValidate: true });
            }
        }
    }, [asks, bids, quantity, side, isRestingLimitOrder]);

    return { limitPriceTooltip, isRestingLimitOrder, resetDefault };
};

export default useLimitOrder;
